import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PurchaseOrderService from "../../../services/purchaseorder.service";
import AdminService from "../../../services/admin.service";
import { Container, Row, Col, Button, Card, Badge, Spinner, Alert } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { ArrowLeft } from 'react-bootstrap-icons';

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-us', {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
};

const getItemSupplier = (item) => {
  if (!item) return 'ignite';
  if (item.printItemId || item.print_item) return 'print';
  const source = item.product?.style?.source;
  if (typeof source === 'string') {
    const normalized = source.toLowerCase();
    if (normalized === 'sanmar' || normalized === 'sands' || normalized === 'ignite' || normalized === 'print') {
      return normalized;
    }
  }
  return 'ignite';
};

const StatusTable = ({ date, status, response, responseTitle, onShowResponse }) => (
  <div className="border rounded overflow-hidden mt-3">
    <div className="px-3 py-2 bg-light border-bottom fw-semibold small text-uppercase text-muted">
      Last Run
    </div>
    <div className="px-3 py-2 d-flex justify-content-between border-bottom">
      <span className="text-muted">Date</span>
      <span>{formatDate(date)}</span>
    </div>
    <div className="px-3 py-2 d-flex justify-content-between border-bottom">
      <span className="text-muted">Status</span>
      <span>{status || '—'}</span>
    </div>
    <div className="px-3 py-2 d-flex justify-content-between align-items-center">
      <span className="text-muted">Response</span>
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={!response}
        onClick={() => onShowResponse(responseTitle, response)}
      >
        View Response
      </Button>
    </div>
  </div>
);

const AdminClosedPODetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { poId: poIdParam } = useParams();
  const poId = poIdParam || location.state?.poId;

  const [po, setPO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [showJson, setShowJson] = useState(false);
  const [response, setResponse] = useState();
  const [responseTitle, setResponseTitle] = useState("Response");

  useEffect(() => {
    if (!poId) {
      setError('Purchase order not found.');
      setLoading(false);
      return;
    }
    async function fetchData() {
      setLoading(true);
      setError('');
      try {
        const result = await PurchaseOrderService.getClosedPurchaseOrder(poId);
        setPO(result.data);
      } catch (err) {
        console.error(err);
        setError('Unable to load purchase order.');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [poId]);

  const suppliers = useMemo(() => {
    const found = { print: false, sanmar: false, sands: false, ignite: false };
    if (!po) return found;
    if (po.isPrintPO) found.print = true;

    const orders = po.orders || [];
    for (const order of orders) {
      for (const item of (order.order_items || [])) {
        found[getItemSupplier(item)] = true;
      }
    }
    return found;
  }, [po]);

  const reloadPO = async () => {
    const result = await PurchaseOrderService.getClosedPurchaseOrder(poId);
    setPO(result.data);
  };

  const downloadPO = async (id) => {
    setActionLoading('download');
    try {
      await AdminService.downloadPO(id);
    } finally {
      setActionLoading('');
    }
  };

  const generatePO = async (id) => {
    setActionLoading('generate');
    try {
      await PurchaseOrderService.generateXLS(id);
    } finally {
      setActionLoading('');
    }
  };

  const submitToSanmar = async (id) => {
    setActionLoading('sanmar');
    try {
      await PurchaseOrderService.submitToSanmar(id);
      await reloadPO();
    } finally {
      setActionLoading('');
    }
  };

  const submitPreflightToSanmar = async (id) => {
    setActionLoading('preflight');
    try {
      await PurchaseOrderService.submitPreflightToSanmar(id);
      await reloadPO();
    } finally {
      setActionLoading('');
    }
  };

  const submitToSands = async (id) => {
    setActionLoading('sands');
    try {
      await PurchaseOrderService.submitToSands(id);
      await reloadPO();
    } finally {
      setActionLoading('');
    }
  };

  const showResponse = (title, body) => {
    setResponseTitle(title);
    setResponse(body);
    setShowJson(true);
  };

  const parseResponse = (body) => {
    if (!body) return '';
    try {
      return JSON.stringify(JSON.parse(body), null, 2);
    } catch {
      return String(body);
    }
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (error || !po) {
    return (
      <Container className="mt-4" style={{ maxWidth: 900 }}>
        <Button variant="link" className="px-0 mb-3" onClick={() => navigate('/admin/closedpos')}>
          <ArrowLeft /> Back to Closed Purchase Orders
        </Button>
        <Alert variant="danger">{error || 'Purchase order not found.'}</Alert>
      </Container>
    );
  }

  const finalizedBy = po.finalizedBy
    ? `${po.finalizedBy.lastname}, ${po.finalizedBy.firstname}`
    : '—';

  return (
    <Container className="mt-4 mb-5" style={{ maxWidth: 960 }}>
      <Button variant="link" className="px-0 mb-2" onClick={() => navigate('/admin/closedpos')}>
        <ArrowLeft /> Back to Closed Purchase Orders
      </Button>

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h1 className="h3 mb-1">Purchase Order #{po.id}</h1>
          <div className="text-muted">
            {po.subsite?.subsiteName ? `${po.subsite.subsiteName} · ` : ''}
            Created {formatDate(po.createdAt)}
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {suppliers.print && <Badge bg="success">Print</Badge>}
          {suppliers.sanmar && <Badge bg="primary">SanMar</Badge>}
          {suppliers.sands && <Badge bg="info">S&amp;S</Badge>}
          {suppliers.ignite && <Badge bg="secondary">Ignite</Badge>}
        </div>
      </div>

      <Card className="mb-3 shadow-sm border-0">
        <Card.Body>
          <Row className="gy-3">
            <Col md={4}>
              <div className="text-muted small">Finalized By</div>
              <div className="fw-semibold">{finalizedBy}</div>
            </Col>
            <Col md={4}>
              <div className="text-muted small">Finalized Date</div>
              <div className="fw-semibold">{formatDate(po.finalizedDate || po.createdAt)}</div>
            </Col>
            <Col md={4}>
              <div className="text-muted small">Orders on PO</div>
              <div className="fw-semibold">{(po.orders || []).length}</div>
            </Col>
          </Row>
          <hr />
          <div className="d-flex flex-wrap gap-2">
            <Button
              variant="success"
              disabled={actionLoading === 'generate'}
              onClick={() => generatePO(po.id)}
            >
              {actionLoading === 'generate' ? 'Generating…' : 'Generate PO XLS'}
            </Button>
            <Button
              variant="outline-success"
              disabled={actionLoading === 'download'}
              onClick={() => downloadPO(po.id)}
            >
              {actionLoading === 'download' ? 'Downloading…' : 'Download XLS'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {suppliers.print && (
        <Card className="mb-3 shadow-sm border-0">
          <Card.Header className="bg-white fw-semibold">
            Print Items
            <Badge bg="success" className="ms-2">Print</Badge>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-0">
              This purchase order contains print items. Use Generate / Download XLS above to process the print PO.
              Supplier submission (SanMar / S&amp;S) is not required for print-only POs.
            </p>
          </Card.Body>
        </Card>
      )}

      {suppliers.sanmar && (
        <Card className="mb-3 shadow-sm border-0">
          <Card.Header className="bg-white fw-semibold d-flex justify-content-between align-items-center">
            <span>
              SanMar
              <Badge bg="primary" className="ms-2">SanMar</Badge>
            </span>
            {po.sentToSanmarDate
              ? <Badge bg="success">Submitted</Badge>
              : <Badge bg="warning" text="dark">Not submitted</Badge>}
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={6}>
                <h6 className="mb-2">Preflight</h6>
                <Button
                  variant="outline-primary"
                  disabled={actionLoading === 'preflight'}
                  onClick={() => submitPreflightToSanmar(po.id)}
                >
                  {actionLoading === 'preflight' ? 'Sending…' : 'Send SanMar Preflight'}
                </Button>
                <StatusTable
                  date={po.preflightDate}
                  status={po.preflightStatus}
                  response={po.preflightResponse}
                  responseTitle="SanMar Preflight Response"
                  onShowResponse={showResponse}
                />
              </Col>
              <Col md={6}>
                <h6 className="mb-2">Submit Order</h6>
                <Button
                  variant="danger"
                  disabled={actionLoading === 'sanmar'}
                  onClick={() => submitToSanmar(po.id)}
                >
                  {actionLoading === 'sanmar' ? 'Sending…' : 'Send To SanMar'}
                </Button>
                <StatusTable
                  date={po.sentToSanmarDate}
                  status={po.sanmarStatus}
                  response={po.sanmarResponse}
                  responseTitle="SanMar Response"
                  onShowResponse={showResponse}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {suppliers.sands && (
        <Card className="mb-3 shadow-sm border-0">
          <Card.Header className="bg-white fw-semibold d-flex justify-content-between align-items-center">
            <span>
              S&amp;S Activewear
              <Badge bg="info" className="ms-2">S&amp;S</Badge>
            </span>
            {po.sentToSandsDate
              ? <Badge bg="success">Submitted</Badge>
              : <Badge bg="warning" text="dark">Not submitted</Badge>}
          </Card.Header>
          <Card.Body>
            <Button
              variant="danger"
              disabled={actionLoading === 'sands'}
              onClick={() => submitToSands(po.id)}
            >
              {actionLoading === 'sands' ? 'Sending…' : 'Send To S&S'}
            </Button>
            <StatusTable
              date={po.sentToSandsDate}
              status={po.sandsStatus}
              response={po.sandsResponse}
              responseTitle="S&S Response"
              onShowResponse={showResponse}
            />
          </Card.Body>
        </Card>
      )}

      {!suppliers.print && !suppliers.sanmar && !suppliers.sands && (
        <Alert variant="secondary">
          No Print, SanMar, or S&amp;S items were found on this purchase order.
          {suppliers.ignite ? ' It appears to contain Ignite items only.' : ''}
        </Alert>
      )}

      <Modal show={showJson} onHide={() => setShowJson(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{responseTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre className="mb-0" style={{ maxHeight: 480, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
            {parseResponse(response)}
          </pre>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AdminClosedPODetails;
